import { useMemo } from 'react';

import { MissionKindEnum } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';

import { Wizard } from '../../components/Wizard';

import { FormVariant } from '../enums/variant.enum';
import { MissionFormTab } from '../Mission/enums/tab.enum';
import { useInitialValuesFactory } from '../Mission/hooks/useInitialValuesFactory.hook';
import { useMissionStepDefinitions } from '../Mission/hooks/useSteps.hook';
import { useMissionSubmit } from '../Mission/hooks/useSubmit.hook';
import { pickSteps } from '../Mission/utils/pick-steps.util';
import type { MissionCreationFormProps } from '../Mission/types/mission-creation-form-props.type';

// Created from its collaboration, a sub-mission does not choose it: its
// actors follow its quotation case.
const SUB_MISSION_CREATION_TABS: readonly MissionFormTab[] = [
  MissionFormTab.Type,
  MissionFormTab.GeneralInfo,
  MissionFormTab.SubMissionActors,
  MissionFormTab.Address,
  MissionFormTab.CodingConfig,
];

export const SubMissionCreationForm = ({
  compositeMissionId,
  folderConsolidation,
}: Readonly<MissionCreationFormProps>) => {
  invariant(
    !!compositeMissionId,
    'A sub-mission is created from its collaboration',
  );

  const { initialValues } = useInitialValuesFactory(
    undefined,
    MissionKindEnum.SubMission,
    compositeMissionId,
  );

  const { handleSubmit } = useMissionSubmit({
    formVariant: FormVariant.Creation,
  });

  const definitions = useMissionStepDefinitions({
    folderConsolidation,
    formVariant: FormVariant.Creation,
    kind: MissionKindEnum.SubMission,
  });
  const { steps, wizardChildren } = useMemo(
    () => pickSteps(definitions, SUB_MISSION_CREATION_TABS),
    [definitions],
  );

  return (
    <Wizard
      askMemoBeforeSubmit
      formVariant={FormVariant.Creation}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      showCancelButton={false}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
